import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/breadcrumb";
import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import {
  useIsSuperAdminOrSuperUser,
  useUserContext,
} from "../../context/UserContext";
import { PushNotifications } from "../../components/PushNotifications/PushNotifications";
import { LicencedProduct } from "../../enums/LicencedProduct";
import { LicencedProductFeatures } from "../../enums/LicencedProductFeatures";
import { PushNotificationTermsAndConditions } from "../../components/PushNotificationTermsAndConditions/PushNotificationTermsAndConditions";
import { useApiClient } from "../../hooks/useApiClient";
import { CherryPayApi } from "../../api/models";

const TERMS_AND_CONDITIONS_KEY: CherryPayApi.AcceptedTermsAndConditionsKey =
  "CherryPass_PushNotifications_20251204";

export const PushNotificationList = () => {
  const { userBusinessId } = useUserContext();
  const isSuperAdminOrSuperUser = useIsSuperAdminOrSuperUser();
  const businessSummary = useBusinessContext();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const isPushNotificationsEnabled = useMemo(() => {
    return (
      (businessSummary?.EnabledFeatures ?? {})[
        LicencedProduct.CherryPass
      ]?.includes(LicencedProductFeatures.CherryPass_PushNotifications) ?? false
    );
  }, [businessSummary]);

  const hasAcceptedTerms = useMemo(() => {
    return (
      businessSummary?.AcceptedTermsAndConditions?.[TERMS_AND_CONDITIONS_KEY] !=
      null
    );
  }, [businessSummary]);

  useEffect(() => {
    if (!(isSuperAdminOrSuperUser && isPushNotificationsEnabled)) {
      navigate(`/businesses/${userBusinessId === "*" ? "" : userBusinessId}`);
    }
  }, [
    isSuperAdminOrSuperUser,
    isPushNotificationsEnabled,
    userBusinessId,
    navigate,
  ]);

  const handleAcceptTerms = async () => {
    const result = await apiClient.acceptTermsAndConditions(
      businessSummary.BusinessId,
      TERMS_AND_CONDITIONS_KEY
    );

    if (result.ok) {
      // Reload the page to refresh the business summary
      window.location.reload();
    } else {
      throw new Error(result.message);
    }
  };

  return (
    <>
      <PageHeading>
        <Breadcrumb>
          {!userBusinessId && (
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/businesses">
                Businesses
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/businesses/${businessSummary.BusinessId}`}
            >
              {businessSummary.DisplayName}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <PageHeading.Title>Push Notifications</PageHeading.Title>
      </PageHeading>
      <PageContent padding={"10px 0 0 0"} background={"white"}>
        {!hasAcceptedTerms ? (
          <PushNotificationTermsAndConditions
            businessId={businessSummary.BusinessId}
            onAccept={handleAcceptTerms}
          />
        ) : (
          <PushNotifications businessId={businessSummary.BusinessId} />
        )}
      </PageContent>
    </>
  );
};
